import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import en from "../locales/en.json";
import ja from "../locales/ja.json";

type Translations = typeof en; // Use English as base type
type Language = "en" | "ja";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Translations> = { en, ja };

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// Helper to determine initial language
const getInitialLanguage = (): Language => {
  // Get browser language (returns something like 'en-US' or 'ja-JP')
  const browserLang = navigator.language.toLowerCase().split("-")[0];

  // Check if browser language is supported, default to 'en' if not
  return browserLang === "ja" ? "ja" : "en";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(getInitialLanguage());

  // Optional: Save preference to localStorage
  useEffect(() => {
    localStorage.setItem("preferredLanguage", language);
  }, [language]);

  const t = (key: string): string => {
    const keys = key.split(".");
    let value: any = translations[language];

    for (const k of keys) {
      value = value?.[k];
    }

    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
